import type {
  ContractFillDraftResponse,
  ContractFillDraftPayload,
  ContractDetailResponse,
  PatchContractRequest,
  ContractFormValues,
} from "@/features/hr/contracts/types";

function toHHMM(time: string | null): string {
  if (!time) return "";
  return time.slice(0, 5);
}

export function draftResponseToFormValues(draft: ContractFillDraftResponse): Partial<ContractFormValues> {
  return {
    sign_date: draft.sign_date ?? "",
    start_date: draft.start_date ?? "",
    work_city_id: draft.work_city_id,
    employee_address_ru: draft.employee_address_ru,
    employee_address_kk: draft.employee_address_kk,
    job_position_ru: draft.job_position_ru,
    job_position_kk: draft.job_position_kk,
    job_duties_ru: draft.job_duties_ru.length > 0 ? draft.job_duties_ru : [""],
    job_duties_kk: draft.job_duties_kk.length > 0 ? draft.job_duties_kk : [""],
    working_days_list: draft.working_days_list as ContractFormValues["working_days_list"],
    work_start_time: toHHMM(draft.work_start_time),
    work_end_time: toHHMM(draft.work_end_time),
    has_break: draft.break_start_time != null && draft.break_end_time != null,
    break_start_time: draft.break_start_time ? toHHMM(draft.break_start_time) : undefined,
    break_end_time: draft.break_end_time ? toHHMM(draft.break_end_time) : undefined,
    salary_amount: draft.salary_amount ?? "",
    trial_period: draft.trial_period,
    trial_duration_months: draft.trial_duration_months ?? undefined,
    work_format: (draft.work_format as ContractFormValues["work_format"]) ?? undefined,
    is_parallel_worker: draft.is_parallel_worker ?? false,
  };
}

export function formValuesToDraftPayload(
  values: ContractFormValues,
  lastActiveSection: string,
): ContractFillDraftPayload {
  return {
    last_active_section: lastActiveSection,
    sign_date: values.sign_date || null,
    start_date: values.start_date || null,
    work_city_id: values.work_city_id,
    work_format: values.work_format ?? null,
    job_position_ru: values.job_position_ru,
    job_position_kk: values.job_position_kk,
    job_duties_ru: values.job_duties_ru,
    job_duties_kk: values.job_duties_kk,
    working_days_list: values.working_days_list,
    work_start_time: values.work_start_time || null,
    work_end_time: values.work_end_time || null,
    break_start_time: values.has_break ? (values.break_start_time || null) : null,
    break_end_time: values.has_break ? (values.break_end_time || null) : null,
    salary_amount: values.salary_amount || null,
    employee_address_ru: values.employee_address_ru,
    employee_address_kk: values.employee_address_kk,
    trial_period: values.trial_period,
    trial_duration_months: values.trial_period ? (values.trial_duration_months ?? null) : null,
    is_parallel_worker: values.is_parallel_worker,
  };
}

export function contractDetailToFormValues(detail: ContractDetailResponse): Partial<ContractFormValues> {
  return {
    contract_number: detail.contract_number ?? "",
    sign_date: detail.sign_date,
    start_date: detail.start_date,
    work_city_id: detail.work_city.id,
    employee_address_ru: detail.employee_address_ru,
    employee_address_kk: detail.employee_address_kk,
    job_position_ru: detail.job_position_ru,
    job_position_kk: detail.job_position_kk,
    job_duties_ru: detail.job_duties_ru.length > 0 ? detail.job_duties_ru : [""],
    job_duties_kk: detail.job_duties_kk.length > 0 ? detail.job_duties_kk : [""],
    working_days_list: detail.working_days_list as ContractFormValues["working_days_list"],
    work_start_time: toHHMM(detail.work_start_time),
    work_end_time: toHHMM(detail.work_end_time),
    has_break: detail.break_start_time != null && detail.break_end_time != null,
    break_start_time: detail.break_start_time ? toHHMM(detail.break_start_time) : undefined,
    break_end_time: detail.break_end_time ? toHHMM(detail.break_end_time) : undefined,
    salary_amount: detail.salary_amount,
    trial_period: detail.trial_period,
    trial_duration_months: detail.trial_duration_months ?? undefined,
    work_format: detail.work_format as ContractFormValues["work_format"],
    is_parallel_worker: detail.is_parallel_worker ?? false,
  };
}

export function formValuesToContractPatch(values: ContractFormValues): PatchContractRequest {
  return {
    contract_number: values.contract_number?.trim() || undefined,
    sign_date: values.sign_date || undefined,
    start_date: values.start_date || undefined,
    work_city_id: values.work_city_id ?? undefined,
    work_format: values.work_format ?? undefined,
    job_position_ru: values.job_position_ru,
    job_position_kk: values.job_position_kk,
    job_duties_ru: values.job_duties_ru,
    job_duties_kk: values.job_duties_kk,
    working_days_list: values.working_days_list,
    work_start_time: values.work_start_time || undefined,
    work_end_time: values.work_end_time || undefined,
    break_start_time: values.has_break ? (values.break_start_time || null) : null,
    break_end_time: values.has_break ? (values.break_end_time || null) : null,
    salary_amount: values.salary_amount || undefined,
    employee_address_ru: values.employee_address_ru,
    employee_address_kk: values.employee_address_kk,
    trial_period: values.trial_period,
    trial_duration_months: values.trial_period ? (values.trial_duration_months ?? null) : null,
    is_parallel_worker: values.is_parallel_worker,
  };
}
